import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useUser } from '../user/UserContext';
import useApi from '../api';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Eye, Globe, Clock, Headset, History, User, MessageCircle, Zap, Lock, MapPin, LayoutGrid, List } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { conLog } from '../utils/conLog';
import { useLocation, useNavigate } from 'react-router-dom';
import Upgrade from '../modals/Upgrade';
import useAnalytics from '../analytics/analytics';
import ExportVisitorsButton from './ExportVisitorsButton';
import PageSizeSelect, { DEFAULT_PAGE_SIZE } from '@/components/ui/PageSizeSelect';
import AimdocLoader from '../ui/AimdocLoader';
import { useTableFilters, DataTableToolbar, useTableSort, SortControl, SortHeader } from '@/components/shared/filters';
import { buildVisitorFilters, VISITOR_SORTS, VISITOR_DEFAULT_SORT } from './liveViewFilters';
import VisitorDrawer from './VisitorDrawer';
import { buildVisitorProfileParams } from './liveViewQuery';
import LiveVisitorCard from './LiveVisitorCard';
import VisitorListIdentityCell from './VisitorListIdentityCell';
import {
  appendBounded,
  buildLiveBoardLanes,
  changedBoardVisitorIds,
  classifyLiveBoardLane,
  dedupeActivity,
  deriveVisitorStatus,
  mergeSnapshotVisitor,
  type LiveBoardLanes,
  type VisitorPresence,
} from './liveViewModel';
import {
  getIdentityDisplayName,
  getIdentitySubtitle,
  getIdentityVerificationTitle,
  getInitials,
  resolveIdentityStatus,
} from '../../lib/visitorIdentity';

/**
 * Body cells of the pinned Actions column. The cell needs an opaque background
 * so scrolled-under columns do not show through, which would also hide the
 * row's translucent hover tint; the pseudo-element re-applies that tint above
 * the opaque background (and below the buttons) when the row is hovered.
 */
const STICKY_ACTIONS_CELL_CLASS =
  "sticky right-0 bg-background before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:content-[''] group-hover:before:bg-muted/50";

interface LiveBrowserEvent {
  id?: string;
  event_type: 'interaction' | 'view_started';
  timestamp: string;
  url?: string;
  page?: string;
  parent_window_url?: string;
  element?: string;
  element_name?: string;
  element_text?: string;
  x?: number;
  y?: number;
}

const MAX_VISITOR_CLICK_EVENTS = 100;
const MAX_VISITOR_PAGE_VIEWS = 50;
const FREE_EMAIL_PROVIDERS = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com", "icloud.com", "mail.com", "protonmail.com", "zoho.com"];

function applyLiveBrowserEvent(visitor: VisitorPresence, liveEvent: LiveBrowserEvent): VisitorPresence {
  if (liveEvent.event_type === 'view_started') {
    const page = liveEvent.page || liveEvent.url;
    if (!page) {
      return visitor;
    }

    const existingPageViews = visitor.page_view_history || [];
    const alreadyPresent = existingPageViews.some((pageView) =>
      pageView.timestamp === liveEvent.timestamp && pageView.page === page
    );
    const nextPageViews = alreadyPresent
      ? existingPageViews
      : [...existingPageViews, { page, timestamp: liveEvent.timestamp }].slice(-MAX_VISITOR_PAGE_VIEWS);

    return {
      ...visitor,
      current_page: liveEvent.url || page || visitor.current_page,
      page_view_history: nextPageViews,
    };
  }

  const parentWindowUrl = liveEvent.parent_window_url || liveEvent.url || visitor.current_page;
  if (!parentWindowUrl) {
    return visitor;
  }

  const existingClicks = visitor.click_events || [];
  const alreadyPresent = existingClicks.some((clickEvent) =>
    clickEvent.timestamp === liveEvent.timestamp
    && clickEvent.parent_window_url === parentWindowUrl
    && clickEvent.element === liveEvent.element
    && clickEvent.element_text === liveEvent.element_text
  );
  const nextClicks = alreadyPresent
    ? existingClicks
    : [
        ...existingClicks,
        {
          x: typeof liveEvent.x === 'number' ? liveEvent.x : 0,
          y: typeof liveEvent.y === 'number' ? liveEvent.y : 0,
          timestamp: liveEvent.timestamp,
          element: liveEvent.element || 'DIV',
          element_name: liveEvent.element_name,
          element_text: liveEvent.element_text,
          parent_window_url: parentWindowUrl,
        },
      ].slice(-MAX_VISITOR_CLICK_EVENTS);

  return {
    ...visitor,
    current_page: parentWindowUrl,
    click_events: nextClicks,
  };
}

function getVisitorFaviconDomain(visitor: VisitorPresence): string | null {
  const company = visitor.identity?.company as Record<string, unknown> | undefined;
  const companyDomain = company?.domain;
  if (typeof companyDomain === 'string' && companyDomain) {
    return companyDomain.replace(/^https?:\/\//, '').split('/')[0];
  }

  const websiteUrl = company?.website_url;
  if (typeof websiteUrl === 'string' && websiteUrl) {
    try {
      const url = websiteUrl.includes('://') ? websiteUrl : `https://${websiteUrl}`;
      return new URL(url).hostname;
    } catch {
      return websiteUrl.replace(/^https?:\/\//, '').split('/')[0];
    }
  }

  const email = visitor.identity?.summary?.email || visitor.identity?.lead?.email;
  if (email && email.includes('@')) {
    const domain = email.split('@')[1];
    if (domain && !FREE_EMAIL_PROVIDERS.includes(domain.toLowerCase())) {
      return domain;
    }
  }

  return null;
}

function getVisitorLabel(visitor: VisitorPresence): string {
  return getIdentityDisplayName({
    lead: visitor.identity?.lead,
    person: visitor.identity?.person,
    company: visitor.identity?.company,
  }) || 'Anonymous';
}

function getVisitorStatusMeta(visitor: VisitorPresence): {
  label: string;
  badgeClassName: string;
  tableTextClassName: string;
  dotClassName: string;
  icon: React.ComponentType<{ className?: string }>;
} {
  switch (deriveVisitorStatus(visitor)) {
    case 'app_session':
      return {
        label: 'App Session',
        badgeClassName: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
        tableTextClassName: 'text-violet-600 dark:text-violet-400',
        dotClassName: 'bg-violet-500 animate-pulse',
        icon: LayoutGrid,
      };
    case 'web_session':
      return {
        label: 'Online (Web Session)',
        badgeClassName: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-200',
        tableTextClassName: 'text-sky-600 dark:text-sky-400',
        dotClassName: 'bg-sky-500 animate-pulse',
        icon: Globe,
      };
    case 'in_app':
      return {
        label: 'In-App',
        badgeClassName: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200',
        tableTextClassName: 'text-indigo-600 dark:text-indigo-400',
        dotClassName: 'bg-indigo-500 animate-pulse',
        icon: LayoutGrid,
      };
    case 'onsite':
      return {
        label: 'Onsite',
        badgeClassName: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
        tableTextClassName: 'text-emerald-600 dark:text-emerald-400',
        dotClassName: 'bg-emerald-500 animate-pulse',
        icon: Eye,
      };
    default:
      return {
        label: 'Offline',
        badgeClassName: 'bg-muted text-muted-foreground',
        tableTextClassName: 'text-muted-foreground',
        dotClassName: 'bg-muted-foreground/40',
        icon: Clock,
      };
  }
}

function VisitorStatusText({ visitor }: { visitor: VisitorPresence }) {
  const meta = getVisitorStatusMeta(visitor);

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${meta.tableTextClassName}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dotClassName}`} />
      {meta.label}
    </span>
  );
}

function VisitorAvatar({
  visitor,
  sizeClass,
  fallbackClassName,
  initialsClassName,
  iconClassName,
}: {
  visitor: VisitorPresence;
  sizeClass: string;
  fallbackClassName: string;
  initialsClassName: string;
  iconClassName: string;
}) {
  const faviconDomain = useMemo(() => getVisitorFaviconDomain(visitor), [visitor]);
  const [faviconError, setFaviconError] = useState(false);

  useEffect(() => {
    setFaviconError(false);
  }, [faviconDomain]);

  const displayName = getVisitorLabel(visitor);
  const initials = displayName === 'Anonymous' ? '' : getInitials(displayName);

  if (faviconDomain && !faviconError) {
    return (
      <div className={`${sizeClass} rounded-md overflow-hidden flex items-center justify-center flex-shrink-0`}>
        <img
          src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(faviconDomain)}&sz=128`}
          alt=""
          className="h-[68%] w-[68%] object-contain rounded-[3px]"
          onError={() => setFaviconError(true)}
        />
      </div>
    );
  }

  return (
    <div className={`${sizeClass} ${fallbackClassName} flex items-center justify-center flex-shrink-0`}>
      {initials ? <span className={initialsClassName}>{initials}</span> : <User className={iconClassName} />}
    </div>
  );
}

const LiveView = () => {
  const { organization, loading, checkLicenseType } = useUser();
  const { connectWebSocket, get, post } = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const { captureEvent } = useAnalytics();
  const [activeVisitors, setActiveVisitors] = useState<Map<string, VisitorPresence>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [repOptions, setRepOptions] = useState<Array<{ value: string; label: string }>>([]);
  const visitorFilters = useMemo(() => buildVisitorFilters(repOptions), [repOptions]);
  const {
    values: filterValues,
    setFilter,
    clearFilter,
    clearAll,
    searchValue,
    setSearch,
  } = useTableFilters(visitorFilters);
  const {
    activeField: sortField,
    activeOrder: sortOrder,
    isDefault: isDefaultSort,
    setSort,
    toggleField,
    clear: clearSort,
  } = useTableSort(VISITOR_DEFAULT_SORT);
  const [presentationOrder, setPresentationOrder] = useState<string[]>([]);
  const [pendingVisitorIds, setPendingVisitorIds] = useState<Set<string>>(new Set());
  const [boardLanes, setBoardLanes] = useState<LiveBoardLanes>({
    contacts: [],
    warm: [],
    new: [],
  });
  const activeVisitorsRef = useRef(activeVisitors);
  const boardLanesRef = useRef(boardLanes);
  const socketRef = useRef<WebSocket | null>(null);
  const shouldReconnectRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const getRef = useRef(get);
  const connectWebSocketRef = useRef(connectWebSocket);
  const snapshotInFlightRef = useRef(false);
  const snapshotQueuedRef = useRef(false);
  const snapshotQueuedReplaceRef = useRef(false);
  const snapshotRequestRef = useRef(0);
  const queryGenerationRef = useRef(0);
  const wsVersionRef = useRef(0);
  const visitorWsVersionRef = useRef(new Map<string, number>());
  const desiredOrderRef = useRef<string[]>([]);
  const latestSnapshotIdsRef = useRef<Set<string>>(new Set());
  const presentationOrderRef = useRef<string[]>([]);
  const locationSearchRef = useRef(location.search);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [startVisitorId, setStartVisitorId] = useState<string | null>(null);
  const [startMessage, setStartMessage] = useState("");
  const [selectedVisitor, setSelectedVisitor] = useState<VisitorPresence | null>(null);
  const initialView = new URLSearchParams(location.search).get('view') === 'board' ? 'board' : 'list';
  const [viewMode, setViewMode] = useState<'board' | 'list'>(initialView);

  // Centralized access check via UserContext
  const hasLiveViewAccess = () => checkLicenseType('pro');
  const hasAccess = hasLiveViewAccess();

  const replaceActiveVisitors = useCallback((next: Map<string, VisitorPresence>) => {
    activeVisitorsRef.current = next;
    setActiveVisitors(next);
  }, []);

  const updateActiveVisitors = useCallback((
    updater: (current: Map<string, VisitorPresence>) => Map<string, VisitorPresence>,
  ) => {
    const next = updater(activeVisitorsRef.current);
    replaceActiveVisitors(next);
    return next;
  }, [replaceActiveVisitors]);

  const replaceBoardLanes = useCallback((next: LiveBoardLanes) => {
    boardLanesRef.current = next;
    setBoardLanes(next);
  }, []);

  useEffect(() => {
    getRef.current = get;
    connectWebSocketRef.current = connectWebSocket;
  }, [get, connectWebSocket]);

  useEffect(() => {
    presentationOrderRef.current = presentationOrder;
  }, [presentationOrder]);

  useEffect(() => {
    locationSearchRef.current = location.search;
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page') || '1', 10);
    setPage(Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1);
    const perPageParam = parseInt(params.get('per_page') || String(DEFAULT_PAGE_SIZE), 10);
    setPerPage(Number.isFinite(perPageParam) && perPageParam > 0 ? perPageParam : DEFAULT_PAGE_SIZE);
    setViewMode(params.get('view') === 'board' ? 'board' : 'list');
  }, [location.search]);

  const updateQueryParams = useCallback((updates: Record<string, string | null>) => {
    const params = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === 'all') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    navigate(`?${params.toString()}`);
  }, [location.search, navigate]);

  useEffect(() => {
    const statuses = new URLSearchParams(location.search).getAll('status');
    if (viewMode === 'board' && statuses.length === 1 && statuses[0] === 'offline') {
      updateQueryParams({ view: 'list' });
    }
  }, [location.search, updateQueryParams, viewMode]);

  const markVisitorMutation = useCallback((visitorId: string) => {
    wsVersionRef.current += 1;
    visitorWsVersionRef.current.set(visitorId, wsVersionRef.current);
  }, []);

  const fetchVisitorList = useCallback(async (
    showLoading: boolean = false,
    reconcile: boolean = false,
  ) => {
    if (snapshotInFlightRef.current) {
      snapshotQueuedRef.current = true;
      if (!reconcile) snapshotQueuedReplaceRef.current = true;
      return;
    }
    snapshotInFlightRef.current = true;
    const requestId = ++snapshotRequestRef.current;
    const requestGeneration = queryGenerationRef.current;
    const requestWsVersion = wsVersionRef.current;
    try {
      if (showLoading) {
        setIsLoading(true);
      }

      const params = new URLSearchParams(locationSearchRef.current);
      const response = await getRef.current(`/visitor-profiles?${buildVisitorProfileParams({
        page: parseInt(params.get('page') || '1', 10),
        perPage: parseInt(params.get('per_page') || String(DEFAULT_PAGE_SIZE), 10),
        search: params.get('search') || '',
        statuses: params.getAll('status'),
        lastSeenFilter: params.get('last_seen_filter') || 'all',
        firstSeenFilter: params.get('first_seen_filter') || 'all',
        identityType: params.get('identity_type') || 'all',
        hasHistory: params.get('has_conversation_history') || 'all',
        humanAgentIds: params.getAll('human_agent_ids'),
        sortBy: (params.get('sort_by') || 'last_seen_at') as any,
        sortOrder: params.get('sort_order') === 'asc' ? 'asc' : 'desc',
        includeActivity: false,
      }).toString()}`);

      if (
        response.data?.items
        && requestId === snapshotRequestRef.current
        && requestGeneration === queryGenerationRef.current
      ) {
        const snapshots = response.data.items as VisitorPresence[];
        const snapshotIds = snapshots.map((visitor) => visitor.visitor_id);
        desiredOrderRef.current = snapshotIds;
        latestSnapshotIdsRef.current = new Set(snapshotIds);
        const current = activeVisitorsRef.current;
        const next = reconcile ? new Map(current) : new Map<string, VisitorPresence>();
        snapshots.forEach((snapshot) => {
          const changedDuringRequest =
            (visitorWsVersionRef.current.get(snapshot.visitor_id) || 0) > requestWsVersion;
          next.set(
            snapshot.visitor_id,
            mergeSnapshotVisitor(current.get(snapshot.visitor_id), snapshot, changedDuringRequest),
          );
        });
        replaceActiveVisitors(next);
        const nextBoardLanes = buildLiveBoardLanes(snapshotIds, next);
        if (reconcile) {
          const currentOrder = presentationOrderRef.current;
          const currentSet = new Set(currentOrder);
          const changedIds = changedBoardVisitorIds(boardLanesRef.current, nextBoardLanes);
          snapshotIds.forEach((id, index) => {
            if (!currentSet.has(id) || currentOrder[index] !== id) changedIds.add(id);
          });
          currentOrder.forEach((id) => {
            if (!latestSnapshotIdsRef.current.has(id)) changedIds.add(id);
          });
          if (changedIds.size) {
            setPendingVisitorIds((current) => {
              let next = current;
              changedIds.forEach((id) => { next = appendBounded(next, id); });
              return next;
            });
          }
        } else {
          setPresentationOrder(snapshotIds);
          replaceBoardLanes(nextBoardLanes);
          setPendingVisitorIds(new Set());
        }
        setPage(response.data.page || 1);
        setTotalPages(response.data.pages || 0);
        conLog('Reconciled visitor profiles snapshot:', snapshots.length);
      }
    } catch (error) {
      console.error('Error fetching visitor profiles snapshot:', error);
      if (showLoading) {
        replaceActiveVisitors(new Map());
        setPresentationOrder([]);
        replaceBoardLanes({ contacts: [], warm: [], new: [] });
        setTotalPages(0);
      }
    } finally {
      snapshotInFlightRef.current = false;
      if (showLoading) {
        setIsLoading(false);
      }
      if (snapshotQueuedRef.current) {
        snapshotQueuedRef.current = false;
        const replace = snapshotQueuedReplaceRef.current;
        snapshotQueuedReplaceRef.current = false;
        void fetchVisitorList(replace, !replace);
      }
    }
  }, [replaceActiveVisitors, replaceBoardLanes]);

  useEffect(() => {
    if (loading) {
      return;
    }
    
    if (!organization?.id || !hasAccess) {
      setIsLoading(false);
      return;
    }

    queryGenerationRef.current += 1;
    desiredOrderRef.current = [];
    latestSnapshotIdsRef.current = new Set();
    setPendingVisitorIds(new Set());
    void fetchVisitorList(true, false);
  }, [organization?.id, organization?.license_type, organization?.trial_end_date, loading, hasAccess, location.search, fetchVisitorList]);

  useEffect(() => {
    if (!organization?.id || !hasAccess) return;
    void getRef.current('/users/').then((response) => {
      if (!Array.isArray(response?.data)) return;
      setRepOptions(response.data.map((user: any) => ({
        value: String(user.id),
        label: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email,
      })));
    }).catch((error) => console.error('Error loading visitor rep filters:', error));
  }, [organization?.id, hasAccess]);

  const queueVisitorPresentationUpdate = useCallback((visitorId: string) => {
    setPendingVisitorIds((current) => appendBounded(current, visitorId));
    if (!desiredOrderRef.current.includes(visitorId)) {
      desiredOrderRef.current = [visitorId, ...desiredOrderRef.current];
      latestSnapshotIdsRef.current.add(visitorId);
    }
  }, []);

  const queueChangedBoardPlacements = useCallback((
    visitors: ReadonlyMap<string, VisitorPresence>,
  ) => {
    const nextBoardLanes = buildLiveBoardLanes(desiredOrderRef.current, visitors);
    const changedIds = changedBoardVisitorIds(boardLanesRef.current, nextBoardLanes);
    if (!changedIds.size) return;
    setPendingVisitorIds((current) => {
      let next = current;
      changedIds.forEach((id) => { next = appendBounded(next, id); });
      return next;
    });
  }, []);

  // Setup WebSocket for real-time updates  
  useEffect(() => {
    if (loading || !organization?.id || !hasAccess) {
      return;
    }

    shouldReconnectRef.current = true;
    reconnectAttemptsRef.current = 0;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const scheduleReconnect = () => {
      if (!shouldReconnectRef.current || reconnectTimerRef.current) return;
      const attempts = reconnectAttemptsRef.current;
      const delay = Math.min(1000 * 2 ** attempts, 30000);
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        reconnectAttemptsRef.current = attempts + 1;
        void initializeWebSocket();
      }, delay);
    };

    const initializeWebSocket = async () => {
      if (
        socketRef.current &&
        (socketRef.current.readyState === WebSocket.OPEN ||
          socketRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      try {
        const ws = await connectWebSocketRef.current(`/ws/organization/${organization.id}`);
        if (!ws) {
          console.error('WebSocket connection failed');
          scheduleReconnect();
          return;
        }

        socketRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            conLog('LiveView WebSocket message:', data);
            
            if (data.type === 'visitor_active') {
              markVisitorMutation(data.visitor_id);
              updateActiveVisitors(prev => {
                const existing = prev.get(data.visitor_id);
                const newMap = new Map(prev);
                const inApp = typeof data.in_app === 'boolean'
                  ? data.in_app
                  : existing?.in_app || false;
                newMap.set(data.visitor_id, {
                  visitor_id: data.visitor_id,
                  agent_id: data.agent_id || existing?.agent_id || '',
                  organization_id: data.organization_id || existing?.organization_id || String(organization.id),
                  current_page: data.current_page || existing?.current_page || 'Unknown',
                  status: 'active',
                  online: true,
                  in_app: inApp,
                  presence_surface: data.presence_surface || (inApp ? 'app' : 'web'),
                  last_seen: data.last_seen || data.timestamp || existing?.last_seen || new Date().toISOString(),
                  click_events: existing?.click_events || [],
                  page_view_history: existing?.page_view_history || [],
                  ip_address: data.ip_address || existing?.ip_address,
                  derived_status: existing?.derived_status,
                  status_label: existing?.status_label,
                  // Preserve existing enriched data if present
                  conversation_id: existing?.conversation_id,
                  session_active: existing?.session_active,
                  conversation_count: existing?.conversation_count,
                  latest_conversation_id: existing?.latest_conversation_id,
                  city: existing?.city,
                  region: existing?.region,
                  country: existing?.country,
                  identity: existing?.identity,
                  human_agent: existing?.human_agent,
                  is_warm: existing?.is_warm,
                  warm_reasons: existing?.warm_reasons,
                  last_seen_at: existing?.last_seen_at,
                  first_seen_at: existing?.first_seen_at,
                });
                return newMap;
              });
              if (!presentationOrderRef.current.includes(data.visitor_id)) {
                queueVisitorPresentationUpdate(data.visitor_id);
              }
              queueChangedBoardPlacements(activeVisitorsRef.current);
            } else if (data.type === 'browser_event') {
              markVisitorMutation(data.visitor_id);
              updateActiveVisitors(prev => {
                const existing = prev.get(data.visitor_id);
                if (!existing || !data.event) {
                  return prev;
                }

                const newMap = new Map(prev);
                const inApp = typeof data.in_app === 'boolean'
                  ? data.in_app
                  : existing.in_app || false;
                const updatedVisitor = applyLiveBrowserEvent(existing, data.event as LiveBrowserEvent);
                newMap.set(data.visitor_id, {
                  ...updatedVisitor,
                  status: 'active',
                  online: true,
                  in_app: inApp,
                  presence_surface: data.presence_surface || (inApp ? 'app' : 'web'),
                  last_seen: data.last_seen || data.timestamp || existing.last_seen,
                  ip_address: data.ip_address || existing.ip_address,
                });
                return newMap;
              });
              queueChangedBoardPlacements(activeVisitorsRef.current);
            } else if (
              data.type === 'visitor_delta_update' ||
              data.type === 'visitor_page_change' ||
              data.type === 'visitor_heartbeat'
            ) {
              markVisitorMutation(data.visitor_id);
              updateActiveVisitors(prev => {
                const existing = prev.get(data.visitor_id);
                if (!existing) {
                  return prev;
                }
                const newMap = new Map(prev);
                const baseClickEvents = existing.click_events || [];
                const basePageViews = existing.page_view_history || [];
                const inApp = typeof data.in_app === 'boolean'
                  ? data.in_app
                  : existing.in_app || false;

                const mergedClickEvents = Array.isArray(data.click_events)
                  ? dedupeActivity(
                      data.click_events,
                      (item: any) => `${item.timestamp}|${item.parent_window_url}|${item.element}|${item.element_text}`,
                      MAX_VISITOR_CLICK_EVENTS,
                    )
                  : (
                    data.type === 'visitor_delta_update' &&
                    Array.isArray(data.new_click_events) &&
                    data.new_click_events.length > 0
                  )
                    ? dedupeActivity(
                        [...baseClickEvents, ...data.new_click_events],
                        (item: any) => `${item.timestamp}|${item.parent_window_url}|${item.element}|${item.element_text}`,
                        MAX_VISITOR_CLICK_EVENTS,
                      )
                    : baseClickEvents;

                const mergedPageViews = Array.isArray(data.page_view_history)
                  ? dedupeActivity(
                      data.page_view_history,
                      (item: any) => `${item.timestamp}|${item.page}`,
                      MAX_VISITOR_PAGE_VIEWS,
                    )
                  : (
                    data.type === 'visitor_delta_update' &&
                    Array.isArray(data.new_page_views) &&
                    data.new_page_views.length > 0
                  )
                    ? dedupeActivity(
                        [...basePageViews, ...data.new_page_views],
                        (item: any) => `${item.timestamp}|${item.page}`,
                        MAX_VISITOR_PAGE_VIEWS,
                      )
                    : basePageViews;

                newMap.set(data.visitor_id, {
                  ...existing,
                  status: 'active',
                  online: true,
                  in_app: inApp,
                  presence_surface: data.presence_surface || (inApp ? 'app' : 'web'),
                  current_page: data.current_page || existing.current_page,
                  last_seen: data.last_seen || data.timestamp || existing.last_seen,
                  click_events: mergedClickEvents,
                  page_view_history: mergedPageViews,
                  ip_address: data.ip_address || existing.ip_address
                });
                return newMap;
              });
              queueChangedBoardPlacements(activeVisitorsRef.current);
            } else if (data.type === 'visitor_inactive') {
              markVisitorMutation(data.visitor_id);
              updateActiveVisitors(prev => {
                const existing = prev.get(data.visitor_id);
                if (!existing) {
                  return prev;
                }
                const newMap = new Map(prev);
                const inApp = typeof data.in_app === 'boolean'
                  ? data.in_app
                  : existing.in_app;
                newMap.set(data.visitor_id, {
                  ...existing,
                  status: 'inactive',
                  online: false,
                  in_app: inApp,
                  presence_surface: data.presence_surface || existing.presence_surface,
                  last_seen: data.last_seen || data.timestamp || existing.last_seen
                });
                return newMap;
              });
              queueChangedBoardPlacements(activeVisitorsRef.current);
            } else if (data.type === 'session_active') {
              const visitorId = data.visitor_id;
              if (visitorId) {
                markVisitorMutation(visitorId);
                updateActiveVisitors(prev => {
                  const existing = prev.get(visitorId);
                  if (!existing) {
                    return prev;
                  }
                  const newMap = new Map(prev);
                  newMap.set(visitorId, {
                    ...existing,
                    conversation_id: data.conversation_id,
                    session_active: true,
                    // A live session implies presence — keep the status cell in
                    // sync with the row indicator instead of waiting on a
                    // separate visitor_active event (which may be stale after a
                    // presence WS reconnect).
                    online: true,
                    status: 'active',
                  });
                  return newMap;
                });
                queueChangedBoardPlacements(activeVisitorsRef.current);
              }
            } else if (data.type === 'session_inactive') {
              const visitorId = data.visitor_id;
              if (visitorId) {
                markVisitorMutation(visitorId);
                updateActiveVisitors(prev => {
                  const existing = prev.get(visitorId);
                  if (!existing) {
                    return prev;
                  }
                  const newMap = new Map(prev);
                  newMap.set(visitorId, {
                    ...existing,
                    conversation_id: undefined,
                    session_active: false
                  });
                  return newMap;
                });
                queueChangedBoardPlacements(activeVisitorsRef.current);
              }
            }
          } catch (error) {
            console.error('Error parsing LiveView WebSocket message:', error);
          }
        };

        ws.onopen = () => {
          setIsConnected(true);
          reconnectAttemptsRef.current = 0;
          void fetchVisitorList(false, true);
        };

        ws.onclose = (event) => {
          console.warn('Disconnected from LiveView organization notifications');
          setIsConnected(false);
          if (socketRef.current === ws) {
            socketRef.current = null;
          }

          if (shouldReconnectRef.current && event.code !== 1000 && event.code !== 1008) {
            scheduleReconnect();
          }
        };

        ws.onerror = (error) => {
          console.error('LiveView WebSocket error:', error);
          setIsConnected(false);
        };
      } catch (error) {
        console.error('Failed to establish LiveView WebSocket connection:', error);
        setIsConnected(false);
        scheduleReconnect();
      }
    };

    void initializeWebSocket();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: 'silent_close_connection' }));
        socketRef.current.close();
      }
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [organization?.id, organization?.license_type, organization?.trial_end_date, loading, hasAccess, fetchVisitorList, markVisitorMutation, queueChangedBoardPlacements, queueVisitorPresentationUpdate, updateActiveVisitors]); // re-run if plan or trial status changes

  // The websocket is authoritative while connected. Poll only as a lightweight
  // fallback while disconnected; opening or reconnecting the socket performs
  // its own one-time reconciliation above.
  useEffect(() => {
    if (loading || !organization?.id || !hasAccess || isConnected) {
      return;
    }

    const intervalId = setInterval(() => {
      void fetchVisitorList(false, true);
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [organization?.id, organization?.license_type, organization?.trial_end_date, loading, hasAccess, isConnected, fetchVisitorList]);

  const formatTimeAgo = (timestamp?: string | null) => {
    if (!timestamp) {
      return '—';
    }
    const now = new Date();
    const time = new Date(timestamp);
    if (Number.isNaN(time.getTime())) {
      return '—';
    }
    const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  };

  const formatLocation = (visitor: VisitorPresence) => {
    const parts = [];
    if (visitor.city) parts.push(visitor.city);
    if (visitor.region) parts.push(visitor.region);
    if (visitor.country) parts.push(visitor.country);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const getVisitorSubtitle = (visitor: VisitorPresence): string | null => {
    return getIdentitySubtitle({
      lead: visitor.identity?.lead,
      person: visitor.identity?.person,
      company: visitor.identity?.company,
    });
  };

  const hasContactIdentity = (visitor: VisitorPresence): boolean => {
    return visitor.identity?.summary?.status === 'contact';
  };

  const getPageTitle = (url?: string | null) => {
    if (!url) {
      return '—';
    }
    try {
      const urlObj = new URL(url);
      return urlObj.pathname === '/' ? 'Homepage' : urlObj.pathname.replace(/\/$/, '').split('/').pop() || 'Unknown Page';
    } catch {
      return url.length > 30 ? `${url.substring(0, 30)}...` : url;
    }
  };

  const openStartModal = (visitorId: string) => {
    setStartVisitorId(visitorId);
    setStartMessage("");
    setStartModalOpen(true);
  };

  const submitStartConversation = async () => {
    if (!startVisitorId) return;
    if (!startMessage.trim()) return;
    try {
      const presence = activeVisitors.get(startVisitorId);
      if (!presence?.online || !presence?.agent_id) {
        toast.error('This visitor is offline right now.', toastConfig);
        return;
      }
      // Track attempt to start a conversation from LiveView
      captureEvent('liveview_start_conversation_attempted', {
        visitor_id: startVisitorId,
        agent_id: presence?.agent_id,
        organization_id: presence?.organization_id || organization?.id,
        current_page: presence?.current_page,
      });
      const resp = await post(`/visitors/${startVisitorId}/start-conversation`, {
        agent_id: presence?.agent_id,
        lead_id: presence?.identity?.lead?.id,
        message: startMessage.trim(),
        ip_address: presence?.ip_address,
        current_page: presence?.current_page,
        click_events: presence?.click_events,
      });
      setStartModalOpen(false);
      const conversationId = resp?.data?.conversation_id;
      // Track success
      captureEvent('liveview_conversation_started', {
        visitor_id: startVisitorId,
        agent_id: presence?.agent_id,
        organization_id: presence?.organization_id || organization?.id,
        conversation_id: conversationId,
        current_page: presence?.current_page,
      });
      if (conversationId) {
        // Sessions started from Live View are already owned by the initiating human agent
        // (see backend `/visitors/{visitor_id}/start-conversation`), so auto_takeover is unnecessary
        // and can leave the session UI stuck in a pending takeover state.
        navigate(`/sessions/${conversationId}`);
      }
    } catch (e) {
      console.error('Failed to start conversation', e);
      // Track failure
      captureEvent('liveview_start_conversation_failed', {
        visitor_id: startVisitorId,
        organization_id: organization?.id,
      });
    }
  };

  const openChat = async (visitorId: string, conversationId: string) => {
    try {
      const presence = activeVisitors.get(visitorId);
      if (!presence?.online) {
        navigate(`/sessions/${conversationId}`);
        return;
      }
      // Track attempt
      captureEvent('liveview_open_chat_attempted', {
        visitor_id: visitorId,
        conversation_id: conversationId,
        agent_id: presence?.agent_id,
        organization_id: presence?.organization_id || organization?.id,
      });
      
      // Send request to open the visitor's chat widget
      await post(`/visitors/${visitorId}/open-chat`, {
        conversation_id: conversationId,
      });
      
      // Track success
      captureEvent('liveview_open_chat_sent', {
        visitor_id: visitorId,
        conversation_id: conversationId,
        agent_id: presence?.agent_id,
        organization_id: presence?.organization_id || organization?.id,
      });
      
      // Wait a moment for the visitor's widget to open and activate the session
      // before navigating. This prevents the "session is not active" error.
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Navigate to the session with auto_takeover enabled
      navigate(`/sessions/${conversationId}?auto_takeover=1`);
    } catch (e) {
      console.error('Failed to open chat', e);
      const statusCode = (e as any)?.response?.status;
      if (statusCode === 409) {
        const detail = (e as any)?.response?.data?.detail;
        toast.error(
          detail || "This is not the visitor's latest session. Open their newest session to chat.",
          toastConfig
        );
      } else {
        toast.error('Failed to open chat. Please try again.', toastConfig);
      }
      // Track failure
      captureEvent('liveview_open_chat_failed', {
        visitor_id: visitorId,
        conversation_id: conversationId,
        organization_id: organization?.id,
      });
    }
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || (totalPages > 0 && nextPage > totalPages)) {
      return;
    }
    updateQueryParams({
      page: String(nextPage),
    });
  };

  const handlePerPageChange = (value: number) => {
    updateQueryParams({
      per_page: String(value),
      page: '1',
    });
  };

  const visitorsList = presentationOrder
    .map((visitorId) => activeVisitors.get(visitorId))
    .filter((visitor): visitor is VisitorPresence => Boolean(visitor));

  const applyPendingUpdates = () => {
    const nextOrder = desiredOrderRef.current.filter((id) => activeVisitors.has(id));
    setPresentationOrder(nextOrder);
    const nextVisitors = new Map<string, VisitorPresence>();
    latestSnapshotIdsRef.current.forEach((id) => {
      const visitor = activeVisitorsRef.current.get(id);
      if (visitor) nextVisitors.set(id, visitor);
    });
    replaceActiveVisitors(nextVisitors);
    replaceBoardLanes(buildLiveBoardLanes(nextOrder, nextVisitors));
    setPendingVisitorIds(new Set());
  };

  const changeViewMode = (mode: 'board' | 'list') => {
    applyPendingUpdates();
    updateQueryParams({ view: mode });
  };

  const boardVisitors = (lane: keyof LiveBoardLanes) => boardLanes[lane]
    .map((visitorId) => activeVisitors.get(visitorId))
    .filter((visitor): visitor is VisitorPresence => Boolean(visitor));
  const leadVisitors = boardVisitors('contacts');
  const warmVisitors = boardVisitors('warm');
  const regularVisitors = boardVisitors('new');

  const isWarmVisitor = (visitor: VisitorPresence) => classifyLiveBoardLane(visitor) === 'warm';

  const renderLiveLane = (
    title: string,
    visitors: VisitorPresence[],
    accent: 'blue' | 'orange' | 'green',
    emptyCopy: string,
  ) => (
    <section className="min-w-0 rounded-xl border bg-muted/20 p-3 flex flex-col gap-3">
      <div className="flex items-center justify-between px-1">
        <h2 className="font-semibold text-sm">{title}</h2>
        <Badge variant="secondary">{visitors.length}</Badge>
      </div>
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-44 w-full rounded-lg" />
          <Skeleton className="h-44 w-full rounded-lg" />
        </div>
      ) : visitors.length ? (
        <div className="space-y-3">
          {visitors.map((visitor) => (
            <LiveVisitorCard
              key={visitor.visitor_id}
              visitor={visitor}
              accent={accent}
              onOpenDetails={() => setSelectedVisitor(visitor)}
              onOpenPrimary={() => {
                if (visitor.session_active && visitor.conversation_id) {
                  navigate(`/sessions/${visitor.conversation_id}`);
                } else if (
                  deriveVisitorStatus(visitor) === 'offline'
                  && visitor.latest_conversation_id
                ) {
                  navigate(`/sessions/${visitor.latest_conversation_id}`);
                } else if (deriveVisitorStatus(visitor) === 'offline') {
                  return;
                } else if (visitor.latest_conversation_id) {
                  navigate(`/sessions/${visitor.latest_conversation_id}`);
                } else {
                  openStartModal(visitor.visitor_id);
                }
              }}
              onViewAllSessions={() => navigate(`/sessions?visitor_id=${visitor.visitor_id}`)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed bg-background p-8 text-center">
          <p className="text-sm font-medium">No one here right now</p>
          <p className="text-xs text-muted-foreground mt-1">{emptyCopy}</p>
        </div>
      )}
    </section>
  );

  // If user doesn't have access, show upgrade overlay
  // Show loading while user/organization data is being fetched
  if (loading) {
    return <AimdocLoader label="Loading live visitors" />;
  }

  // If user doesn't have access, show upgrade overlay
  if (!hasLiveViewAccess()) {
    return (
      <div className="h-full min-h-0 w-full flex flex-col p-6 bg-background overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Eye className="h-6 w-6" />
            Visitor View
          </h1>
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Pro Feature</span>
          </div>
        </div>

        {/* Blurred preview content */}
        <div className="h-full relative flex-1 overflow-hidden">
          <div className="absolute inset-0 backdrop-blur-sm z-10 flex items-center justify-center">
            <Card className="max-w-md mx-auto shadow-lg border-2">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Eye className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Visitor View</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-muted-foreground">
                  Watch your website visitors browse in real-time and see their journey through your site.
                </p>
                <div className="space-y-2 text-sm text-left">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span>Real-time visitor tracking</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    <span>See active chat sessions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full" />
                    <span>View visitor journey & clicks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full" />
                    <span>Monitor page navigation</span>
                  </div>
                </div>
                <Button 
                  onClick={() => setUpgradeModalVisible(true)}
                  className="w-full"
                  size="lg"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Upgrade to Engage
                </Button>
                <p className="text-xs text-muted-foreground">
                  Available on Engage and Convert plans
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Upgrade Modal */}
        <Upgrade 
          showModal={upgradeModalVisible} 
          setShowModal={setUpgradeModalVisible}
          sessionId={null}
        />
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 w-full flex flex-col p-6 bg-background overflow-hidden">
      <div className="flex flex-col gap-3 mb-4 shrink-0">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <DataTableToolbar
            className="grow"
            filters={visitorFilters}
            values={filterValues}
            onChange={setFilter}
            onClear={clearFilter}
            onClearAll={clearAll}
            search={{ value: searchValue, onChange: setSearch, placeholder: 'Search visitors...' }}
          />
          <SortControl
            sorts={VISITOR_SORTS}
            activeField={sortField}
            activeOrder={sortOrder}
            isDefault={isDefaultSort}
            onSelect={setSort}
            onClear={clearSort}
          />
          <ExportVisitorsButton
            params={new URLSearchParams(location.search)}
          />
          <div className="flex items-center gap-3 ml-auto shrink-0">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm text-muted-foreground">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            {pendingVisitorIds.size > 0 && (
              <Button size="sm" variant="secondary" onClick={applyPendingUpdates}>
                Show {pendingVisitorIds.size} {pendingVisitorIds.size === 1 ? 'update' : 'updates'}
              </Button>
            )}
            <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && changeViewMode(value as 'board' | 'list')}>
              <ToggleGroupItem value="list" aria-label="List view" className="px-3">
                <List className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="board" aria-label="Board view" className="px-3">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      </div>

      {viewMode === 'board' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 flex-1 min-h-0 overflow-auto">
        {renderLiveLane('Contacts', leadVisitors, 'blue', 'Known contacts who are live appear here.')}
        {renderLiveLane('Warm', warmVisitors, 'orange', 'Engaged live visitors appear here.')}
        {renderLiveLane('New', regularVisitors, 'green', 'New live visitors appear here.')}
      </div>
      ) : (
        /* List View */
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[240px]">Visitor</TableHead>
                  <TableHead className="w-[100px]">Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Current Page</TableHead>
                  <TableHead className="w-[100px]">
                    <SortHeader label="Sessions" field="conversation_count" activeField={sortField} activeOrder={sortOrder} onSort={toggleField} />
                  </TableHead>
                  <TableHead className="w-[120px]">
                    <SortHeader label="First seen" field="first_seen_at" activeField={sortField} activeOrder={sortOrder} onSort={toggleField} />
                  </TableHead>
                  <TableHead className="w-[120px]">
                    <SortHeader label="Last seen" field="last_seen_at" activeField={sortField} activeOrder={sortOrder} onSort={toggleField} />
                  </TableHead>
                  <TableHead className="w-[140px] sticky right-0 bg-background">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-2 w-2 rounded-full" />
                          <Skeleton className="h-5 w-16 rounded-md" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-7 w-7 rounded-full" />
                          <div className="space-y-1.5">
                            <Skeleton className="h-4 w-28" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Skeleton className="h-5 w-14 rounded-md" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell>
                        <div className="space-y-1.5">
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-3 w-36" />
                        </div>
                      </TableCell>
                      <TableCell><Skeleton className="h-4 w-6" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell className={STICKY_ACTIONS_CELL_CLASS}><Skeleton className="h-7 w-24 rounded-md" /></TableCell>
                    </TableRow>
                  ))
                ) : visitorsList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <Globe className="h-12 w-12 mx-auto mb-2 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">No visitors found</p>
                      <p className="text-sm text-muted-foreground">Try adjusting the status or last seen filters.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  visitorsList.map((visitor) => {
                    const category = hasContactIdentity(visitor) ? 'lead' : isWarmVisitor(visitor) ? 'warm' : 'new';
                    const categoryLabel = category === 'lead' ? 'Contact' : category === 'warm' ? 'Warm' : 'New';
                    const categoryColor = category === 'lead'
                      ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20'
                      : category === 'warm'
                        ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20'
                        : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20';
                    const location = formatLocation(visitor);
                    const identityStatus = resolveIdentityStatus({
                      summaryStatus: visitor.identity?.summary?.status,
                      lead: visitor.identity?.lead,
                      person: visitor.identity?.person,
                      company: visitor.identity?.company,
                    });

                    return (
                      <TableRow
                        key={visitor.visitor_id}
                        className={`group cursor-pointer hover:bg-muted/50 border-l-2 ${
                          visitor.session_active
                            ? 'border-l-green-500'
                            : visitor.online
                              ? 'border-l-blue-500'
                              : 'border-l-transparent'
                        }`}
                        onClick={() => setSelectedVisitor(visitor)}
                      >
                        <TableCell>
                          <VisitorStatusText visitor={visitor} />
                        </TableCell>

                        <TableCell>
                          <VisitorListIdentityCell
                            visitor={visitor}
                            status={identityStatus}
                            subtitle={getVisitorSubtitle(visitor)}
                            verifiedTitle={getIdentityVerificationTitle({
                              leadIdentityVerifiedAt: visitor.identity?.lead?.identity_verified_at,
                              sessionIdentityVerifiedAt: visitor.identity?.visitor_profile?.identity_verified_at,
                            })}
                            avatar={(
                              <VisitorAvatar
                                visitor={visitor}
                                sizeClass="h-7 w-7"
                                fallbackClassName="rounded-full bg-gradient-to-br from-blue-500 to-purple-500"
                                initialsClassName="text-sm font-semibold text-white"
                                iconClassName="h-4 w-4 text-muted-foreground"
                              />
                            )}
                          />
                        </TableCell>

                        <TableCell>
                          <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${categoryColor}`}>
                            {categoryLabel}
                          </span>
                        </TableCell>

                        <TableCell>
                          {location ? (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate max-w-[150px]">{location}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>

                        <TableCell>
                          {visitor.current_page ? (
                            <div className="max-w-[200px]">
                              <div className="text-sm font-medium truncate">{getPageTitle(visitor.current_page)}</div>
                              <div className="text-xs text-muted-foreground truncate">{visitor.current_page}</div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>

                        <TableCell>
                          {visitor.conversation_count !== undefined && visitor.conversation_count > 0 ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/sessions?visitor_id=${visitor.visitor_id}`);
                              }}
                              aria-label={`View ${visitor.conversation_count} sessions`}
                              title="View all sessions"
                            >
                              <History className="h-3.5 w-3.5" />
                              {visitor.conversation_count}
                            </Button>
                          ) : (
                            <span className="inline-flex h-7 items-center gap-1 px-2 text-xs text-muted-foreground">
                              <History className="h-3.5 w-3.5 opacity-40" />
                              0
                            </span>
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatTimeAgo(visitor.first_seen_at)}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatTimeAgo(visitor.last_seen || visitor.last_seen_at)}
                          </div>
                        </TableCell>

                        <TableCell className={STICKY_ACTIONS_CELL_CLASS}>
                          <div className="flex flex-wrap items-center gap-1">
                          {visitor.online && visitor.session_active && visitor.conversation_id ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/sessions/${visitor.conversation_id}`);
                              }}
                            >
                              <Headset className="h-3 w-3 mr-1" />
                              Take Over Chat
                            </Button>
                          ) : visitor.online && visitor.latest_conversation_id ? (
                            // Take over from the session view; the visitor's
                            // chat reopens on the rep's first message, not on
                            // click (matches Slack takeover behavior).
                            <Button
                              size="sm"
                              className="text-xs h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/sessions/${visitor.latest_conversation_id}`);
                              }}
                            >
                              <Headset className="h-3 w-3 mr-1" />
                              Take Over Chat
                            </Button>
                          ) : visitor.online ? (
                            <Button
                              size="sm"
                              className="text-xs h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                openStartModal(visitor.visitor_id);
                              }}
                            >
                              Start Chat
                            </Button>
                          ) : visitor.latest_conversation_id ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/sessions/${visitor.latest_conversation_id}`);
                              }}
                            >
                              <Headset className="h-3 w-3 mr-1" />
                              Take Over Chat
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7"
                              disabled
                            >
                              Offline
                            </Button>
                          )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <PageSizeSelect value={perPage} onChange={handlePerPageChange} />
            {totalPages > 1 && (
              <div className="flex items-center gap-4">
                <Button variant="outline" onClick={() => handlePageChange(page - 1)} disabled={page <= 1}>
                  Previous
                </Button>
                <span className="text-sm font-medium text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button variant="outline" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}>
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={startModalOpen} onOpenChange={setStartModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Conversation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">First message</label>
              <Input value={startMessage} onChange={(e) => setStartMessage(e.target.value)} placeholder="Hi there! Do you have any questions?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStartModalOpen(false)}>Cancel</Button>
            <Button onClick={submitStartConversation} disabled={!startMessage.trim()}>
              Start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visitor Details Drawer (anonymous / non-lead visitors) */}
      <VisitorDrawer
        visitor={selectedVisitor ? activeVisitors.get(selectedVisitor.visitor_id) || selectedVisitor : null}
        onClose={() => setSelectedVisitor(null)}
        onContactSynced={() => {
          void fetchVisitorList(false, true);
        }}
        onNavigateToSession={(conversationId) => {
          setSelectedVisitor(null);
          navigate(`/sessions/${conversationId}`);
        }}
        onOpenChat={(visitorId, conversationId) => {
          setSelectedVisitor(null);
          openChat(visitorId, conversationId);
        }}
        onStartChat={(visitorId) => {
          setSelectedVisitor(null);
          openStartModal(visitorId);
        }}
        onViewSessions={(visitorId) => {
          setSelectedVisitor(null);
          navigate(`/sessions?visitor_id=${visitorId}`);
        }}
      />
    </div>
  );
};

export default LiveView;
